/* A subject as a person reads it: `priya.nair@bank.in` as "Priya Nair".
 *
 * **Display only, and never a key.** The subject is what the audit chain records and what a filter
 * matches on; this is the line a card prints beside "Turned on by". Two different subjects can
 * render the same name, which is acceptable here and would not be anywhere the value is compared.
 *
 * A platform actor (`system:scheduler`) reads as its role rather than as a mangled address, because
 * "Turned on by System:scheduler" would read as a broken name rather than as the scheduler.
 */
export function personName(subject: string): string {
  const trimmed = subject.trim();
  if (trimmed === '') return trimmed;
  /* Already a name — an IdP that emits `name` rather than an address gives "A. Rao", and splitting
     that on its full stop would print "A Rao". Whitespace is what no address or actor id contains. */
  if (/\s/.test(trimmed) && !trimmed.includes('@')) return trimmed;
  if (!trimmed.includes('@') && trimmed.includes(':')) {
    return capitalise(trimmed.slice(trimmed.indexOf(':') + 1).replace(/[._-]+/g, ' '));
  }
  const local = trimmed.split('@')[0] ?? trimmed;
  const words = local.split(/[._-]+/).filter((word) => word !== '');
  return words.length === 0 ? trimmed : words.map(capitalise).join(' ');
}

function capitalise(word: string): string {
  return word === '' ? word : `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}
